/**
 * TaskLogPanel — "Så gjorde vi här" (kunskapslogg per moment).
 *
 * Props:
 *   task_id      — det moment loggen gäller för (obligatoriskt)
 *   task_name    — visas i rubriken
 *   currentUser  — { user_id, role } — styr delete-knappen
 *
 * Data hämtas via `GET /api/tasks/{task_id}/log-entries`.
 * Nya poster skapas via `POST` (multipart: text + files upp till 5, 15 MB/fil).
 * Radering är begränsad till författaren (av backend) eller admin.
 */
import { useEffect, useRef, useState } from "react";
import { Camera, VideoCamera, Upload, Trash, Paperclip, CaretDown, CaretUp, MagnifyingGlass, EnvelopeSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import api, { API } from "@/lib/api";
import SendLogEntryMailModal from "@/components/SendLogEntryMailModal";

const MAX_TEXT = 1000;
const MAX_FILES = 5;
const MAX_FILE_MB = 15;

const formatDate = (iso) => {
  try {
    return format(new Date(iso), "d MMM yyyy · HH:mm", { locale: sv });
  } catch {
    return iso;
  }
};

const isImage = (mime) => (mime || "").startsWith("image/");
const isVideo = (mime) => (mime || "").startsWith("video/");

/** Fetch preview-token för en bilaga och returnera signad URL. */
async function getPreviewUrl(task_id, entry_id, file_id, inline = 1) {
  const r = await api.post(
    `/tasks/${task_id}/log-entries/${entry_id}/files/${file_id}/preview-token`
  );
  const token = r.data.token;
  return `${API}/tasks/${task_id}/log-entries/${entry_id}/files/${file_id}/download?t=${token}&inline=${inline}`;
}

export function AttachmentThumb({ task_id, entry_id, attachment }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getPreviewUrl(task_id, entry_id, attachment.file_id)
      .then((u) => !cancelled && setUrl(u))
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [task_id, entry_id, attachment.file_id]);

  if (!url) {
    return (
      <div className="w-16 h-16 rounded-md bg-surface-hover border border-app flex items-center justify-center text-[10px] text-text-muted">
        …
      </div>
    );
  }
  if (isImage(attachment.mime)) {
    return (
      <a href={url} target="_blank" rel="noreferrer" title={attachment.original_name}>
        <img
          src={url}
          alt={attachment.original_name}
          loading="lazy"
          className="w-16 h-16 rounded-md object-cover border border-app hover:opacity-90 transition"
        />
      </a>
    );
  }
  if (isVideo(attachment.mime)) {
    return (
      <a href={url} target="_blank" rel="noreferrer" title={attachment.original_name}>
        <div className="w-16 h-16 rounded-md bg-black/80 border border-app flex items-center justify-center text-white text-[10px]">
          🎥 film
        </div>
      </a>
    );
  }
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      className="w-16 h-16 rounded-md border border-app flex items-center justify-center text-[10px] text-text-muted hover:bg-surface-hover"
      title={attachment.original_name}
    >
      📎 fil
    </a>
  );
}

export default function TaskLogPanel({ task_id, task_name, currentUser, object_id, object_name }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [text, setText] = useState("");
  const [picked, setPicked] = useState([]); // File[]
  const [submitting, setSubmitting] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [activeSearch, setActiveSearch] = useState("");
  const [mailingEntry, setMailingEntry] = useState(null);
  const photoRef = useRef(null);
  const videoRef = useRef(null);
  const fileRef = useRef(null);

  const load = async (searchQuery = activeSearch) => {
    if (!task_id) return;
    try {
      const params = { limit: 50 };
      const q = (searchQuery || "").trim();
      if (q) params.search = q;
      if (object_id) params.object_id = object_id;
      const r = await api.get(`/tasks/${task_id}/log-entries`, { params });
      setEntries(r.data || []);
    } catch (e) {
      // silent — panelen är hjälp-funktion, ingen kritisk data
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    setLoading(true);
    setExpanded(false);
    setSearchOpen(false);
    setSearchText("");
    setActiveSearch("");
    load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [task_id, object_id]);

  // Debounca sökning – 300ms efter senaste knapptryck
  useEffect(() => {
    if (!searchOpen) return;
    const q = searchText.trim();
    if (q === activeSearch) return;
    const h = setTimeout(() => {
      setActiveSearch(q);
      setLoading(true);
      load(q);
    }, 300);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, searchOpen]);

  const onPickFiles = (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    const merged = [...picked, ...arr].slice(0, MAX_FILES);
    const oversized = merged.find((f) => f.size > MAX_FILE_MB * 1024 * 1024);
    if (oversized) {
      toast.error(`"${oversized.name}" är större än ${MAX_FILE_MB} MB`);
      return;
    }
    if (picked.length + arr.length > MAX_FILES) {
      toast.info(`Max ${MAX_FILES} filer per post — behåller de första`);
    }
    setPicked(merged);
  };

  const removePicked = (idx) => setPicked((p) => p.filter((_, i) => i !== idx));

  const submit = async ({ mailAfter = false } = {}) => {
    const t = text.trim();
    if (!t && picked.length === 0) {
      toast.info("Skriv en text eller bifoga minst en fil");
      return;
    }
    if (t.length > MAX_TEXT) {
      toast.error(`Texten är max ${MAX_TEXT} tecken`);
      return;
    }
    try {
      setSubmitting(true);
      const fd = new FormData();
      fd.append("text", t);
      if (object_id) fd.append("object_id", object_id);
      picked.forEach((f) => fd.append("files", f, f.name));
      const r = await api.post(`/tasks/${task_id}/log-entries`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const savedEntry = r.data;
      toast.success("Sparat i loggen ✅");
      setText("");
      setPicked([]);
      if (photoRef.current) photoRef.current.value = "";
      if (videoRef.current) videoRef.current.value = "";
      if (fileRef.current) fileRef.current.value = "";
      await load();
      setExpanded(true);
      if (mailAfter && savedEntry?.entry_id) {
        setMailingEntry({
          entry_id: savedEntry.entry_id,
          task_name: task_name || "Moment",
          object_name: object_name || "",
          author_name: savedEntry.author_name || currentUser?.name || "Okänd",
        });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte spara posten");
    } finally {
      setSubmitting(false);
    }
  };

  const removeEntry = async (entry_id) => {
    if (!window.confirm("Radera denna post permanent?")) return;
    try {
      await api.delete(`/tasks/${task_id}/log-entries/${entry_id}`);
      toast.success("Posten raderad");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte radera");
    }
  };

  const canDelete = (entry) =>
    currentUser?.role === "admin" || entry.author_user_id === currentUser?.user_id;

  const visible = expanded ? entries : entries.slice(0, 3);

  return (
    <div
      data-testid="task-log-panel"
      className="mt-4 border border-[var(--brand-forest)]/25 rounded-lg p-4 bg-[var(--brand-forest)]/5"
    >
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <label className="text-xs uppercase tracking-wider text-[var(--brand-forest)] flex items-center gap-1.5">
          <span aria-hidden>🛠</span> Så gjorde vi här
          <span className="text-text-muted normal-case tracking-normal ml-1">
            — {task_name || "detta moment"}
          </span>
        </label>
        <div className="flex items-center gap-2">
          {entries.length > 0 && !activeSearch && (
            <span className="text-[11px] text-text-muted">
              {entries.length} {entries.length === 1 ? "post" : "poster"}
            </span>
          )}
          <button
            type="button"
            data-testid="task-log-search-toggle"
            onClick={() => {
              setSearchOpen((v) => {
                const next = !v;
                if (!next) {
                  setSearchText("");
                  if (activeSearch) {
                    setActiveSearch("");
                    setLoading(true);
                    load("");
                  }
                }
                return next;
              });
            }}
            className={`p-1.5 rounded-md border ${searchOpen ? "bg-[var(--brand-forest)]/15 border-[var(--brand-forest)]/40" : "border-app hover:bg-surface-hover"} text-[var(--brand-forest)]`}
            title={searchOpen ? "Stäng sökning" : "Sök i loggen"}
            aria-label="Sök i loggen"
          >
            <MagnifyingGlass size={14} />
          </button>
        </div>
      </div>

      {searchOpen && (
        <div className="mt-2">
          <div className="relative">
            <MagnifyingGlass
              size={14}
              className="absolute left-2 top-1/2 -translate-y-1/2 text-text-muted pointer-events-none"
            />
            <input
              data-testid="task-log-search-input"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Sök på ord, material, verktyg, färg eller rum…"
              className="tk-input !pl-7 !py-1.5 !text-sm border-[var(--brand-forest)]/30 focus:border-[var(--brand-forest)]"
              autoFocus
            />
          </div>
          {activeSearch && (
            <div className="mt-1 text-[11px] text-text-muted">
              {entries.length} träff{entries.length === 1 ? "" : "ar"} på "{activeSearch}"
            </div>
          )}
        </div>
      )}

      {/* Formulär */}
      <textarea
        data-testid="task-log-text-input"
        value={text}
        onChange={(e) => setText(e.target.value.slice(0, MAX_TEXT))}
        rows={2}
        placeholder="Skriv hur ni löste det — knep, mått, tips för nästa gång…"
        className="tk-input mt-2 border-[var(--brand-forest)]/30 focus:border-[var(--brand-forest)]"
      />
      <div className="mt-1 flex items-center justify-between text-[11px] text-text-muted">
        <span>Max {MAX_TEXT} tecken · syns för alla i teamet</span>
        <span>{text.length}/{MAX_TEXT}</span>
      </div>

      {/* Bilagor pending */}
      {picked.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-2">
          {picked.map((f, i) => (
            <div
              key={i}
              className="inline-flex items-center gap-1.5 text-[11px] bg-surface border border-app rounded-full pl-2 pr-1 py-0.5"
              title={f.name}
            >
              <Paperclip size={11} />
              <span className="truncate max-w-[130px]">{f.name}</span>
              <button
                onClick={() => removePicked(i)}
                className="p-0.5 rounded-full hover:bg-surface-hover text-text-muted"
                aria-label={`Ta bort ${f.name}`}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <label
            className={`tk-btn-outline !text-xs !px-2.5 !py-1 inline-flex items-center gap-1 cursor-pointer ${submitting ? "opacity-60 pointer-events-none" : ""}`}
            data-testid="task-log-camera-photo-label"
            title="Ta ett foto"
          >
            <Camera size={12} /> Ta foto
            <input
              ref={photoRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => onPickFiles(e.target.files)}
            />
          </label>
          <label
            className={`tk-btn-outline !text-xs !px-2.5 !py-1 inline-flex items-center gap-1 cursor-pointer ${submitting ? "opacity-60 pointer-events-none" : ""}`}
            data-testid="task-log-camera-video-label"
            title="Spela in en film"
          >
            <VideoCamera size={12} /> Film
            <input
              ref={videoRef}
              type="file"
              accept="video/*"
              capture="environment"
              className="hidden"
              onChange={(e) => onPickFiles(e.target.files)}
            />
          </label>
          <label
            className={`tk-btn-outline !text-xs !px-2.5 !py-1 inline-flex items-center gap-1 cursor-pointer ${submitting ? "opacity-60 pointer-events-none" : ""}`}
            data-testid="task-log-upload-label"
            title={`Ladda upp fil (max ${MAX_FILES} st, ${MAX_FILE_MB} MB/st)`}
          >
            <Upload size={12} /> Ladda upp
            <input
              ref={fileRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => onPickFiles(e.target.files)}
            />
          </label>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="task-log-save-btn"
            disabled={submitting || (!text.trim() && picked.length === 0)}
            onClick={() => submit()}
            className="tk-btn-primary !text-xs !px-3 !py-1.5"
          >
            {submitting ? "Sparar…" : "Spara post"}
          </button>
          <button
            data-testid="task-log-save-and-mail-btn"
            disabled={submitting || (!text.trim() && picked.length === 0)}
            onClick={() => submit({ mailAfter: true })}
            className="tk-btn-secondary !text-xs !px-3 !py-1.5 inline-flex items-center gap-1.5"
            title="Spara och skicka som mail till kollegor"
          >
            <EnvelopeSimple size={12} weight="bold" />
            Spara & maila
          </button>
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div className="mt-3 text-[11px] text-text-muted italic">Laddar…</div>
      ) : entries.length === 0 ? (
        <div className="mt-3 text-[11px] text-text-muted italic">
          {activeSearch
            ? `Ingen post matchar "${activeSearch}".`
            : "Ingen post ännu — bli den första som delar hur ni gjorde här."}
        </div>
      ) : (
        <>
          <ul className="mt-3 space-y-2" data-testid="task-log-entries">
            {visible.map((e) => (
              <li
                key={e.entry_id}
                data-testid={`task-log-entry-${e.entry_id}`}
                className="bg-surface border border-app rounded-md p-3"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs text-text-muted">
                    <span className="font-medium text-text-primary">{e.author_name}</span>
                    <span className="mx-1.5">·</span>
                    <span>{formatDate(e.created_at)}</span>
                  </div>
                  {canDelete(e) && (
                    <button
                      data-testid={`task-log-delete-${e.entry_id}`}
                      onClick={() => removeEntry(e.entry_id)}
                      className="text-text-muted hover:text-[var(--status-danger)] p-1"
                      title="Radera post"
                    >
                      <Trash size={14} />
                    </button>
                  )}
                </div>
                {e.text && (
                  <p className="text-sm mt-1 whitespace-pre-line break-words">{e.text}</p>
                )}
                {Array.isArray(e.attachments) && e.attachments.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {e.attachments.map((a) => (
                      <AttachmentThumb
                        key={a.file_id}
                        task_id={task_id}
                        entry_id={e.entry_id}
                        attachment={a}
                      />
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ul>
          {entries.length > 3 && (
            <button
              data-testid="task-log-toggle-expand"
              onClick={() => setExpanded((v) => !v)}
              className="mt-2 text-xs text-[var(--brand-forest)] hover:underline inline-flex items-center gap-1"
            >
              {expanded ? (
                <>
                  <CaretUp size={12} /> Visa färre
                </>
              ) : (
                <>
                  <CaretDown size={12} /> Visa alla ({entries.length})
                </>
              )}
            </button>
          )}
        </>
      )}

      {mailingEntry && (
        <SendLogEntryMailModal
          entry={mailingEntry}
          onClose={() => setMailingEntry(null)}
        />
      )}
    </div>
  );
}
